import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import Layout from "../components/Layout";
import {
  FaHome,
  FaGraduationCap,
  FaCalendarAlt,
  FaFileAlt,
  FaUsers,
  FaCheckCircle,
  FaBook,
  FaSchool,
  FaChild,
  FaUserGraduate,
  FaUserTie,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaArrowRight,
  FaDownload,
  FaClock,
  FaQuestionCircle,
  FaListAlt,
  FaMoneyBill,
  FaShieldAlt,
  FaStar
} from "react-icons/fa";
import BreadCrumbImage from "../assets/images/breadcrumb.png";

const Admissions = () => {
  const [activeTab, setActiveTab] = useState("process");

  const admissionProcess = [
    {
      step: "01",
      title: "Inquiry",
      description: "Visit school or website for information",
      icon: FaQuestionCircle,
      color: "from-blue-500 to-cyan-500"
    },
    {
      step: "02",
      title: "Application",
      description: "Fill admission form with documents",
      icon: FaFileAlt,
      color: "from-emerald-500 to-teal-500"
    },
    {
      step: "03",
      title: "Interaction",
      description: "Student & parent interaction session",
      icon: FaUsers,
      color: "from-purple-500 to-pink-500"
    },
    {
      step: "04",
      title: "Confirmation",
      description: "Fee payment & admission confirmation",
      icon: FaCheckCircle,
      color: "from-amber-500 to-orange-500"
    }
  ];

  const eligibility = [
    {
      grade: "Pre-Nursery",
      age: "2.5 - 3.5 years",
      requirements: ["Birth Certificate", "4 Passport Photos"],
      icon: FaChild
    },
    {
      grade: "Nursery to UKG",
      age: "3.5 - 5.5 years",
      requirements: ["Birth Certificate", "Transfer Certificate", "Photos"],
      icon: FaChild
    },
    {
      grade: "Class 1 to 5",
      age: "5.5 - 10 years",
      requirements: ["Transfer Certificate", "Report Card", "Photos", "Aadhar Card"],
      icon: FaUserGraduate
    },
    {
      grade: "Class 6 to 8",
      age: "10 - 13 years",
      requirements: ["Transfer Certificate", "Report Card", "Photos", "Aadhar Card", "Caste Certificate"],
      icon: FaUserGraduate
    },
    {
      grade: "Class 9 to 10",
      age: "13 - 15 years",
      requirements: ["Transfer Certificate", "Marksheet", "Photos", "Aadhar Card", "Caste Certificate", "Migration Certificate"],
      icon: FaUserTie
    }
  ];

  const requiredDocuments = [
    { name: "Birth Certificate", required: "All classes", icon: FaFileAlt },
    { name: "Transfer Certificate", required: "Class 1 onwards", icon: FaSchool },
    { name: "Previous School Marksheet", required: "Class 1 onwards", icon: FaBook },
    { name: "Aadhar Card", required: "All students", icon: FaShieldAlt },
    { name: "Passport Size Photos", required: "6 copies", icon: FaUsers },
    { name: "Caste Certificate", required: "If applicable", icon: FaListAlt }
  ];

  const feeStructure = [
    { item: "Admission Fee", amount: "One-time", description: "Payable at admission" },
    { item: "Tuition Fee", amount: "Quarterly", description: "Per academic quarter" },
    { item: "Development Fee", amount: "Annual", description: "Infrastructure development" },
    { item: "Transport Fee", amount: "Monthly", description: "Optional bus service" },
    { item: "Uniform & Books", amount: "Annual", description: "As per school list" }
  ];

  const importantDates = [
    { event: "Admission Process Starts", date: "December 1, 2025", status: "upcoming" },
    { event: "Last Date for Applications", date: "March 31, 2026", status: "upcoming" },
    { event: "Interaction Sessions", date: "January - March 2026", status: "ongoing" },
    { event: "Academic Year Begins", date: "April 1, 2026", status: "future" }
  ];

  const faqs = [
    { question: "What is the admission age criteria?", answer: "Pre-Nursery: 2.5-3.5 years, Nursery: 3.5-4.5 years, and so on as per CBSE guidelines." },
    { question: "Is there any entrance test?", answer: "For Pre-Nursery to UKG: Interaction only. For Class 1 onwards: Basic aptitude assessment." },
    { question: "What are the school timings?", answer: "8:30 AM to 2:30 PM (Monday to Saturday). Saturday is a working day." },
    { question: "Is transport facility available?", answer: "Yes, GPS-enabled buses with trained attendants cover all major areas." },
    { question: "What is the teacher-student ratio?", answer: "We maintain 1:25 ratio for personalized attention and better learning outcomes." }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Admissions | School</title>
        <meta name="description" content="Admission process, eligibility criteria, required documents, and fee structure for our school." />
      </Helmet>

      {/* Breadcrumb */}
      <div className="relative bg-gray-900">
        <div className="h-40 md:h-48">
          <img src={BreadCrumbImage} alt="Breadcrumb" className="object-cover w-full h-full opacity-60" />
          <div className="absolute inset-0 bg-black/50" />
        </div>

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="flex items-center mb-2 text-xs text-white">
            <Link to="/" className="flex items-center transition-colors hover:text-gray-200">
              <FaHome className="w-3 h-3 mr-1" />
              Home
            </Link>
            <span className="mx-1">/</span>
            <span className="font-semibold">Admissions</span>
          </div>
          <h1 className="text-2xl font-bold text-white md:text-3xl">Admissions 2026-27</h1>
          <p className="mt-1 text-xs text-gray-300">Start your educational journey with us</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container px-3 py-6 mx-auto md:px-4 md:py-8">
        
        {/* Introduction */}
        <motion.div 
          className="max-w-3xl mx-auto mb-8 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="mb-3 text-2xl font-bold text-gray-900">Welcome to Our School</h2>
          <p className="text-gray-700">
            We are now accepting applications for the academic year 2026-27. Our admission process is designed 
            to be transparent and student-friendly. Below you'll find all necessary information about eligibility, 
            process, and requirements.
          </p>
        </motion.div>

        {/* Tabs Navigation */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2 mb-6">
            {["process", "eligibility", "documents", "fees", "dates"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === tab 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab === "process" && "Admission Process"}
                {tab === "eligibility" && "Eligibility"}
                {tab === "documents" && "Documents"}
                {tab === "fees" && "Fee Structure"}
                {tab === "dates" && "Important Dates"}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-6 bg-white border border-gray-200 rounded-xl">
            {/* Admission Process */}
            {activeTab === "process" && (
              <motion.div 
                className="grid gap-6 md:grid-cols-2 lg:grid-cols-4"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {admissionProcess.map((step, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="relative p-5 text-center transition-shadow border border-gray-200 rounded-xl hover:shadow-md"
                  >
                    <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br ${step.color} text-white text-lg font-bold mb-4`}>
                      {step.step}
                    </div>
                    <div className="mb-3">
                      <step.icon className="w-8 h-8 mx-auto text-gray-700" />
                    </div>
                    <h3 className="mb-2 font-bold text-gray-900">{step.title}</h3>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </motion.div>
                ))}
              </motion.div>
            )}

            {/* Eligibility */}
            {activeTab === "eligibility" && (
              <motion.div 
                className="space-y-4"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {eligibility.map((item, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="p-4 transition-shadow border border-gray-200 rounded-lg hover:shadow-sm"
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-blue-50">
                        <item.icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-col mb-2 md:flex-row md:items-center md:justify-between">
                          <h3 className="font-bold text-gray-900">{item.grade}</h3>
                          <span className="text-sm font-medium text-blue-600">Age: {item.age}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {item.requirements.map((req, idx) => (
                            <span key={idx} className="px-3 py-1 text-xs text-gray-700 bg-gray-100 rounded-full">
                              {req}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}

            {/* Documents */}
            {activeTab === "documents" && (
              <motion.div 
                className="grid gap-4 md:grid-cols-2"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {requiredDocuments.map((doc, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="flex items-center gap-3 p-4 transition-shadow border border-gray-200 rounded-lg hover:shadow-sm"
                  >
                    <div className="p-2 rounded-lg bg-blue-50">
                      <doc.icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-bold text-gray-900">{doc.name}</h3>
                      <p className="text-xs text-gray-600">Required: {doc.required}</p>
                    </div>
                    <FaCheckCircle className="w-5 h-5 text-green-500" />
                  </motion.div>
                ))}
              </motion.div>
            )}

            {/* Fee Structure */}
            {activeTab === "fees" && (
              <motion.div 
                className="space-y-4"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                  <div className="flex items-center gap-2 mb-2">
                    <FaMoneyBill className="w-5 h-5 text-blue-600" />
                    <h3 className="font-bold text-gray-900">Fee Structure 2026-27</h3>
                  </div>
                  <p className="mb-4 text-sm text-gray-700">
                    Fees are structured to provide quality education with transparency. 
                    Detailed fee breakup available in the admission office.
                  </p>
                </div>

                {feeStructure.map((fee, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                  >
                    <div>
                      <h4 className="font-bold text-gray-900">{fee.item}</h4>
                      <p className="text-sm text-gray-600">{fee.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-blue-600">{fee.amount}</div>
                      <button className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-700">
                        Details →
                      </button>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}

            {/* Important Dates */}
            {activeTab === "dates" && (
              <motion.div 
                className="space-y-4"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {importantDates.map((date, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="flex items-center justify-between p-4 transition-shadow border border-gray-200 rounded-lg hover:shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <FaCalendarAlt className={`w-5 h-5 ${
                        date.status === 'upcoming' ? 'text-blue-500' :
                        date.status === 'ongoing' ? 'text-green-500' : 'text-gray-500'
                      }`} />
                      <div>
                        <h4 className="font-bold text-gray-900">{date.event}</h4>
                        <p className="text-sm text-gray-600">{date.date}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      date.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                      date.status === 'ongoing' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {date.status}
                    </span>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>
        </div>

        {/* Quick Contact */}
        <motion.div 
          className="p-6 mb-8 text-white bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0">
              <h3 className="mb-2 text-xl font-bold">Need Help with Admission?</h3>
              <p className="text-blue-100">Our admission team is here to assist you</p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <a href="tel:+911234567890" className="flex items-center justify-center gap-2 px-4 py-3 font-bold text-blue-700 bg-white rounded-lg hover:bg-blue-50">
                <FaPhone className="w-4 h-4" />
                Call Now
              </a>
              <a href="mailto:admissions@school.edu" className="flex items-center justify-center gap-2 px-4 py-3 font-bold text-white bg-transparent border-2 border-white rounded-lg hover:bg-white/10">
                <FaEnvelope className="w-4 h-4" />
                Email Us
              </a>
            </div>
          </div>
        </motion.div>

        {/* FAQs */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-1 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"></div>
            <h2 className="text-xl font-bold text-gray-900">Frequently Asked Questions</h2>
            <div className="w-8 h-1 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500"></div>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="overflow-hidden border border-gray-200 rounded-lg">
                <button className="flex items-center justify-between w-full p-4 text-left bg-gray-50 hover:bg-gray-100">
                  <span className="font-medium text-gray-900">{faq.question}</span>
                  <FaArrowRight className="w-4 h-4 text-gray-500" />
                </button>
                <div className="p-4 bg-white">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Download Section */}
        <div className="p-6 mb-8 border border-gray-200 bg-gradient-to-br from-gray-50 to-white rounded-xl">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0">
              <div className="flex items-center gap-3 mb-2">
                <FaDownload className="w-6 h-6 text-blue-600" />
                <h3 className="text-lg font-bold text-gray-900">Download Forms</h3>
              </div>
              <p className="text-sm text-gray-600">Get admission forms and information brochures</p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                Download Admission Form
              </button>
              <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                View Fee Structure
              </button>
            </div>
          </div>
        </div>

        {/* Visit Campus */}
        <div className="p-6 border bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 rounded-xl">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center">
            <div className="lg:w-2/3">
              <div className="flex items-center gap-3 mb-3">
                <FaMapMarkerAlt className="w-6 h-6 text-emerald-600" />
                <h3 className="text-xl font-bold text-gray-900">Schedule Campus Visit</h3>
              </div>
              <p className="mb-4 text-gray-700">
                Experience our campus firsthand. Schedule a visit to see our facilities, 
                meet our faculty, and get a feel of our learning environment.
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FaClock className="w-4 h-4" />
                <span>Visits available: Monday to Friday, 9 AM to 3 PM</span>
              </div>
            </div>
            <div className="lg:w-1/3">
              <button className="w-full px-6 py-3 font-bold text-white rounded-lg bg-emerald-600 hover:bg-emerald-700">
                Schedule Visit
              </button>
            </div>
          </div>
        </div>

        {/* Final Note */}
        <div className="p-4 mt-8 text-center">
          <p className="text-sm text-gray-600">
            * All admissions are subject to availability of seats and fulfillment of eligibility criteria.
          </p>
        </div>

      </div>
    </Layout>
  );
};

export default Admissions;